import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { snackbarType } from "constants/snackbarType";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { addNotification } from "actions/actionCreators/notificationActions";
import * as rightsCodes from "constants/rightsCodes";
import { hasAuthorization } from "utils/functions";
import { useHistory } from "react-router";
import { Button, Typography, useToast } from "@totalsoft/rocket-ui";
import { Box } from "@mui/material";
import { useReactiveVar } from "@apollo/client";
import snackbarVar from "./snackbarVar";

const ToastNotifier = ({ userRights }) => {
  const [t] = useTranslation();
  const addToast = useToast();
  const history = useHistory();
  const hasViewLogsPermission = hasAuthorization(rightsCodes.MASTER_PARTNER_MENU_LOGS, userRights);
  const dispatch = useDispatch();
  const snackbar = useReactiveVar(snackbarVar);

  const redirectToLog = useCallback(
    correlationId => () => {
      history.push(`/logs/${correlationId}`, { reload: true });
    },
    [history]
  );

  const addNotificationFromToast = useCallback(
    (message, usePrefix) => dispatch(addNotification(message, message, usePrefix, true)),
    [dispatch]
  );

  const errorToast = useMemo(() => {
    if (!snackbar) return <></>;
    return (
      <div>
        {hasViewLogsPermission && (
          <Typography>
            {snackbar.message}
            {snackbar.correlationId && (
              <span>
                <br />
                <span>{`${t("Notifier.CorrelationId")}:`}</span>{" "}
                <Typography
                  variant="span"
                  onClick={redirectToLog(snackbar.correlationId)}
                  sx={{ color: "blue", cursor: "pointer" }}
                >{`${snackbar.correlationId}`}</Typography>
              </span>
            )}
          </Typography>
        )}
        {!hasViewLogsPermission && (
          <Typography>
            {snackbar.message}
            {snackbar.correlationId && (
              <>
                <br />
                <span>{`${t("Notifier.CorrelationId")}:`}</span>{" "}
                <Typography sx={{ color: "blue", cursor: "pointer" }}>{`${snackbar.correlationId}`}</Typography>
              </>
            )}
          </Typography>
        )}
        <Box display="flex" gap={1} mt={1}>
          <Button
            variant="text"
            color="white"
            onClick={async () =>
              await navigator.clipboard.writeText(
                `${snackbar.message ?? ""}${
                  snackbar.correlationId ? ` ${t("Notifier.CorrelationId")}: ${snackbar.correlationId}` : ""
                }`
              )
            }
          >
            {t("Copy")}
          </Button>
          <Button
            variant="text"
            color="white"
            onClick={() =>
              addNotificationFromToast(
                `${snackbar.message ?? ""}${
                  snackbar.correlationId ? ` ${t("Notifier.CorrelationId")}: ${snackbar.correlationId}` : ""
                }`,
                false
              )
            }
          >
            {t("AddToNotifications")}
          </Button>
        </Box>
      </div>
    );
  }, [addNotificationFromToast, snackbar, t, redirectToLog, hasViewLogsPermission]);

  const warningToast = useMemo(() => {
    if (!snackbar) return <></>;
    return hasViewLogsPermission ? (
      <p>
        {snackbar.message}
        <Typography sx={{ color: "blue", cursor: "pointer" }} onClick={redirectToLog(snackbar.correlationId)}>
          {snackbar.correlationId}
        </Typography>
      </p>
    ) : (
      <p>
        {snackbar.message}
        <Typography sx={{ color: "blue", cursor: "initial" }}>{snackbar.correlationId}</Typography>
      </p>
    );
  }, [snackbar, redirectToLog, hasViewLogsPermission]);

  useEffect(() => {
    if (!snackbar) return;

    switch (snackbar.type) {
      case snackbarType.error:
        addToast(errorToast, "error", { autoClose: true });
        break;
      case snackbarType.info:
        addToast(snackbar.message, "info", { autoClose: true });
        break;
      case snackbarType.warning:
        addToast(warningToast, "warning", { autoClose: true });
        break;
      default:
        break;
    }

    snackbarVar(null);
  }, [snackbar, addToast, errorToast, warningToast]);

  return null;
};

ToastNotifier.propTypes = {
  userRights: PropTypes.array
};

function mapStateToProps(state) {
  return {
    userRights: state.userRights.data
  };
}

export default connect(mapStateToProps)(ToastNotifier);
